import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Avatar, Box, Collapse, IconButton, InputBase, Menu, MenuItem, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useProgramContext } from '../../../../hooks/useProgramContext';
import { Bone, fadeInSx } from '../../../../components/ui/DashboardSkeletons';
import ElliottWorking from '../../../../components/ui/ElliottWorking';
import DosenTooltip from '../../../../components/ui/DosenTooltip';
import { useLearningGapsData } from '../Program/components/learning-lifecycle/useLearningGapsData';
import { LEVEL_COLOR, LEVEL_NAME } from '../Program/components/learning-lifecycle/CapabilityGapsList';
import { LEVEL_ORDER, type CapabilityLevel } from '../Program/components/learning-lifecycle/teamCapability';
import {
  addCapability,
  importCapabilities,
  applyDraft,
  canConfirmCapabilities,
  canConfirmLevels,
  capabilityNameProblem,
  confirmCapabilities,
  confirmLevels,
  confirmedIdsOf,
  fillLevels,
  isSettled,
  levelsDone,
  levelsSet,
  MAX_CAPABILITIES,
  MAX_CRITERIA,
  MAX_NAME,
  newRole,
  removeCapability,
  renameCapability,
  renameRole,
  roleProblem,
  setCriteria,
  setupSteps,
  type LevelCriteria,
  type SetupCapability,
  type SetupRole,
  type SetupStep,
  type SetupTeam
} from './capabilitySetup';
import CreationFlowDialog, { type CreationKind } from './CreationFlowDialog';
import ImportCapabilitiesDialog from './ImportCapabilitiesDialog';
import SharedCapabilityDialog from './SharedCapabilityDialog';
import {
  clashToImport,
  sharedLevelEdits,
  sharedWith,
  suggestionNamesTaken,
  teamNameProblem,
  withImported,
  withOwnNames,
  type SharedEdit
} from './sharedCapabilities';
import InviteTeamDialog from './InviteTeamDialog';
import { creationFlowsReady, trainingFlowReady } from './flags';
import { ActionButton } from './designSystem';
import { apiMessage, capabilityApi, namesSecondSkill, type LevelCheckView } from '../../../../api/capability';
import { useQuery } from '@tanstack/react-query';
import { listNames } from './create/common';
import { useCapabilityOrg } from '../../../../queries/useCapability';
import { useCapabilityRoles } from './useCapabilityRoles';
import { useAdminAccess } from '../../../../hooks/useAdminAccess';
import { SHARED_KINDS, SOURCE_LABEL, sourceOf } from './capabilitySource';
import SharedSetView, { type SharedBuildPreset } from './SharedSetView';
import { appliedTo, isRoleUsable, kindOfRoleId, sharedNameProblem, sharedSetId } from './sharedSets';
import { useCapabilitySets } from './useCapabilitySets';
import { MOBILE_SX } from '../../../../mobile/mobileApp';

import {
  AddForm,
  BODY,
  BRANDY,
  BRANDY_FRAME,
  BRANDY_TINT,
  CapabilityRow,
  chipSx,
  DIVIDE,
  DONE,
  ElliottNote,
  FONT,
  EmptyState,
  LABEL,
  LINE,
  PageSkeleton,
  RoleHeader,
  SECTION_TITLE,
  SetupStepper,
  SURFACE,
  TEXT,
  TEXT_2,
  TEXT_3,
  TeamSwitch,
  type AddResult
} from './setupParts';

/**
 * Capability Setup (Admin Toolkit): where a team's roles, each role's
 * capabilities and each capability's level criteria are defined, one team and
 * one role at a time. It is the starting point for everything else: an
 * Assessment or Training picks its capabilities from what is set up here.
 *
 * One layout for an org admin (many teams) and a team manager (usually one).
 * The top bar is the navigation: ROLES | team | the team's roles, each marked
 * once it is set up (confirmed, and ready); the team's setup stepper;
 * and the standing actions (Run Assessment, Publish Training once its hand-off
 * is built (see flags.ts), Invite Team), there on every visit. Below it, the role in view: its capabilities, each opening
 * onto its four levels. Edits to them are held until Save Changes; the
 * confirmation for the stage the role is at sits along the bottom.
 */
const CapabilitySetupPage: React.FC = () => {
  const { orgId, coId, isPlatformAdmin } = useProgramContext();
  const navigate = useNavigate();
  // The teams and their roles, and how a change to a role is saved (the backend; see useCapabilityRoles).
  const {
    roles,
    teams: setupTeams,
    teamsList,
    mode,
    loadError,
    reload,
    refresh,
    createRole,
    saveRole,
    deleteRole,
    isSaving
  } = useCapabilityRoles(orgId, isPlatformAdmin);
  // DUMMY: the browser-only fallback has no answer from the backend to list the
  // teams, so the Learning Gaps teams stand in, all open to set up and with no
  // managers to name. Fetched in that mode only.
  const localTeams = useLearningGapsData(mode === 'local', isPlatformAdmin ? orgId : undefined);
  const teams: SetupTeam[] = useMemo(
    () =>
      mode === 'local'
        ? localTeams.teams.map((t) => ({ id: t.id, name: t.name, canEdit: true, managers: [] }))
        : setupTeams,
    [mode, localTeams.teams, setupTeams]
  );
  const editableTeams = useMemo(() => teams.filter((t) => t.canEdit), [teams]);
  // The org admin's shared sets, Development and Standards (./sharedSets.ts): read by everyone who
  // opens the page (a team's roles carry the ones applied to them), edited by org admins only.
  const { access } = useAdminAccess();
  const orgAdmin = isPlatformAdmin || Boolean(access?.orgAdmin);
  const capabilitySets = useCapabilitySets(orgId, isPlatformAdmin);
  const { sets } = capabilitySets;
  // A set saved can make a role ready or not (R4): the roles, with the backend's `ready`, are read again.
  const saveSet: typeof capabilitySets.saveSet = async (kind, change) => {
    const ok = await capabilitySets.saveSet(kind, change);
    if (ok) refresh();
    return ok;
  };
  // What Build Assessment / Build Training open with from a shared set.
  const [sharedPreset, setSharedPreset] = useState<SharedBuildPreset | null>(null);
  // Run Assessment and Invite Team save to the backend; Publish Training waits on its hand-off (see flags.ts).
  const flowsReady = creationFlowsReady();
  const trainingReady = trainingFlowReady();
  const org = useCapabilityOrg();
  // Elliott drafts levels and suggests capabilities (the backend); off once the backend says it is switched off.
  const [elliottOn, setElliottOn] = useState(true);
  const [elliottError, setElliottError] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [creating, setCreating] = useState<CreationKind | null>(null);
  const [inviting, setInviting] = useState(false);
  // Import Capability From Other Role, for the role in view.
  const [importing, setImporting] = useState(false);

  // Unsaved edits to a role's capabilities and levels, by role id, with the saved role they started from.
  const [drafts, setDrafts] = useState<Record<string, { base: SetupRole; role: SetupRole }>>({});
  // Every team of the organization, in its own order. Teams the viewer can't
  // set up are listed too, marked and not selectable.
  const teamChoices = useMemo(
    () =>
      teams.map((t) => {
        const count = roles.filter((r) => r.teamId === t.id).length;
        return t.canEdit
          ? { ...t, disabled: false, meta: count === 0 ? 'No roles yet' : `${count} ${count === 1 ? 'role' : 'roles'}` }
          : { ...t, disabled: true, meta: 'Not your team' };
      }),
    [teams, roles]
  );
  // A bell item ("My role isn't listed") opens the page on its team.
  const location = useLocation();
  const askedTeamId = (location.state as { teamId?: string } | null)?.teamId ?? null;
  const [teamId, setTeamId] = useState<string | null>(askedTeamId);
  useEffect(() => {
    if (askedTeamId) setTeamId(askedTeamId);
  }, [askedTeamId]);
  // The shared sets, under Shared in the switcher: for org admins, once there are sets to show.
  const sharedChoices =
    orgAdmin && capabilitySets.available
      ? SHARED_KINDS.map((kind) => {
          const count = sets.find((s) => s.kind === kind)?.capabilities.length ?? 0;
          return {
            id: sharedSetId(kind),
            name: SOURCE_LABEL[kind],
            meta: count === 0 ? 'No capabilities yet' : `${count} ${count === 1 ? 'capability' : 'capabilities'}`
          };
        })
      : [];
  const sharedKind = sharedChoices.length > 0 ? kindOfRoleId(teamId) : null;
  const [roleId, setRoleId] = useState<string | null>(null);
  const [addingRole, setAddingRole] = useState(false);
  const [draftingRoleId, setDraftingRoleId] = useState<string | null>(null);

  // The chosen team, held unless it goes away; until then, the first one the viewer can set up.
  const team = editableTeams.find((t) => t.id === teamId) ?? editableTeams[0] ?? null;
  const teamRoles = useMemo(() => roles.filter((r) => r.teamId === team?.id), [roles, team?.id]);
  // One role at a time: the chosen one, else the team's first.
  const saved = teamRoles.find((r) => r.id === roleId) ?? teamRoles[0] ?? null;
  const draft = saved ? drafts[saved.id]?.role : undefined;
  const dirty = Boolean(draft);
  // What is on screen: the unsaved edits if there are any. The name is never part of a draft.
  const role = saved && draft ? { ...draft, name: saved.name } : saved;
  // A save of the role in view is on its way: its edits and confirmations wait for it.
  const saving = Boolean(saved && isSaving(saved.id));
  // Why the edits can't be saved as they stand (the backend's rules), when they can't.
  // Within a team a name means one capability: a name given here that another role uses for its own is refused.
  const teamProblemOf = (capabilityId: string) => {
    if (!role || !saved || !dirty) return null;
    const own = teamNameProblem(roles, saved, role, capabilityId);
    if (own) return own;
    // One name, one capability: not the name of a shared one the organization applies in this team.
    const name = role.capabilities.find((c) => c.id === capabilityId)?.name ?? '';
    return sharedNameProblem(roles, sets, saved, name);
  };
  // The organization's shared capabilities that are part of the role in view: read-only here.
  const applied = saved ? appliedTo(saved, sets) : [];
  const problem =
    role && dirty
      ? roleProblem(role, saved) ?? role.capabilities.map((c) => teamProblemOf(c.id)).find(Boolean) ?? null
      : null;
  // The shared capabilities whose levels a save would change, each waiting on a name of its own.
  const [sharedToRename, setSharedToRename] = useState<SharedEdit[]>([]);
  // DA-5042: Elliott checks the levels before they are confirmed, and his check is
  // kept per capability with the texts he read: a verdict is only about those words.
  const [levelChecks, setLevelChecks] = useState<
    Record<string, { criteria: Partial<Record<CapabilityLevel, string>>; levels: LevelCheckView[] }>
  >({});
  const [checkingLevels, setCheckingLevels] = useState(false);
  const [levelCheckError, setLevelCheckError] = useState<string | null>(null);
  // Elliott's own suggestions the admin took, by "capabilityId:level": as long as the level reads so, it passes.
  const [acceptedFixes, setAcceptedFixes] = useState<Record<string, string>>({});
  // The last check Elliott stored for each capability, shown while its levels still read the same.
  const storedLevelChecks = useQuery({
    queryKey: ['capability', 'level-checks', org || ''],
    queryFn: () => capabilityApi.storedLevelChecks(org),
    staleTime: 60_000
  });
  const steps = setupSteps(teamRoles, {
    elliott: elliottOn,
    invited: Boolean(team?.invited),
    applied: (r) => appliedTo(r, sets).length
  });
  // The roles that can be assessed, trained on and invited to (R4), as the backend marks them `ready`:
  // confirmed by their manager (with only shared capabilities, their capabilities), with something
  // counting on them. Ready for good once confirmed. The same roles Invite Team and Update Team offer.
  const readyRoles = teamRoles.filter((r) => isRoleUsable(r, sets));

  /** The roles that share a capability of the role in view, as it was last saved. */
  const sharedNames = (capabilityId: string) => {
    const held = saved?.capabilities.find((c) => c.id === capabilityId);
    return saved && held ? sharedWith(roles, saved, held).map((r) => r.name) : [];
  };

  const dropDraft = (id: string) =>
    setDrafts((current) => {
      const { [id]: _dropped, ...rest } = current;
      return rest;
    });
  /** An edit to the role in view: held as a draft until Save Changes. A null result (a refused add) changes nothing. */
  const editRole = (change: (r: SetupRole) => SetupRole | null): boolean => {
    // Nothing changes while Elliott is drafting the role's levels.
    if (!role || !saved || draftingRoleId === role.id) return false;
    const result = change(role);
    if (!result) return false;
    setDrafts((current) => ({ ...current, [role.id]: { base: current[role.id]?.base ?? saved, role: result } }));
    return true;
  };
  /**
   * Saves the role's edits. The draft goes once the save has gone through, and
   * only if nothing was typed into it meanwhile; a refused save keeps it.
   */
  const saveChanges = async (ownNames?: Record<string, string>) => {
    if (!role || !saved || !draft || problem) return;
    const { id } = role;
    const sent = drafts[id];
    // A shared capability given other levels is no longer the one the other roles hold: it is
    // saved as this role's own, and must be renamed first so the two are not confused.
    if (!ownNames) {
      const edits = sharedLevelEdits(roles, saved, sent.role);
      if (edits.length > 0) {
        setSharedToRename(edits);
        return;
      }
    }
    const final = ownNames ? withOwnNames(sent.role, ownNames) : sent.role;
    // Checked again with the new names in, so a role the backend would refuse is never sent (the edits are kept).
    const refused = ownNames
      ? roleProblem(final, saved) ??
        final.capabilities.map((c) => teamNameProblem(roles, saved, final, c.id)).find(Boolean)
      : null;
    if (refused) {
      toast.error(refused);
      return;
    }
    const ok = await saveRole(id, (latest) => applyDraft(latest, sent.base, final));
    if (!ok) return;
    setDrafts((current) => {
      if (current[id] !== sent) return current;
      const { [id]: _saved, ...rest } = current;
      return rest;
    });
  };

  const addRole = async (name: string): Promise<AddResult> => {
    if (!team) return false;
    // Checked here first, so a blank or a repeat is refused at once; the backend checks again.
    const draftRole = newRole(roles, team.id, name);
    if (!draftRole) return false;
    const created = await createRole(team.id, draftRole.name, draftRole);
    if (!created.ok) return created.message;
    setRoleId(created.role.id);
    setAddingRole(false);
    return true;
  };
  const selectRole = (id: string) => {
    setRoleId(id);
    setAddingRole(false);
  };

  // Elliott's levels for these capabilities of a role, by name (lower case); empty when it can't.
  const draftLevelsFor = async (roleName: string, teamName: string, names: string[]) => {
    if (!elliottOn || names.length === 0) return new Map<string, LevelCriteria>();
    try {
      const drafted = await capabilityApi.draftLevels({ roleName, teamName, capabilities: names }, org);
      setElliottError(null);
      return new Map(drafted.capabilities.map((c) => [c.name.toLowerCase(), c.criteria]));
    } catch (err: any) {
      if (err?.response?.status === 503) setElliottOn(false);
      setElliottError(`${apiMessage(err, 'Elliott could not draft the levels.')} Write them below.`);
      return new Map<string, LevelCriteria>();
    }
  };

  // Confirming the capabilities is what sets Elliott drafting their levels. Only levels the
  // manager hasn't written are filled. A role with only shared capabilities has none of its own to
  // draft: confirming them confirms its levels too, and it is set up.
  const confirmRoleCapabilities = async (id: string) => {
    const current = roles.find((r) => r.id === id);
    if (current && current.capabilities.length === 0) {
      const shared = appliedTo(current, sets).length;
      await saveRole(id, (r) => confirmCapabilities(r, undefined, shared));
      return;
    }
    if (!elliottOn || !current) {
      await saveRole(id, (r) => confirmCapabilities(r));
      return;
    }
    setDraftingRoleId(id);
    if (!(await saveRole(id, (r) => ({ ...r, capabilitiesConfirmed: true })))) {
      setDraftingRoleId(null);
      return;
    }
    const teamName = teams.find((t) => t.id === current.teamId)?.name ?? '';
    const drafted = await draftLevelsFor(
      current.name,
      teamName,
      current.capabilities.map((c) => c.name)
    );
    if (drafted.size > 0)
      await saveRole(id, (r) => confirmCapabilities(r, (name) => drafted.get(name.toLowerCase()) ?? {}));
    setDraftingRoleId(null);
  };

  // The page fills the space under the app header (the dashboard's own measure),
  // so the main section keeps its bottom bar in view and scrolls above it.
  const PAGE = {
    // No padding of its own: the app shell (ProgramDetailsWrapper) already pads every page by 25px.
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    // 86px = the app header; 50px = the shell's 25px padding, top and bottom.
    height: 'calc(100vh - 86px - 50px)',
    minHeight: 0,
    boxSizing: 'border-box',
    // A phone: the page grows with its content and the app's scroll area scrolls it.
    [MOBILE_SX]: { height: 'auto', gap: '12px' }
  };
  const CARD = { bgcolor: SURFACE, border: `1px solid ${LINE}`, borderRadius: '8px' };
  // The page's root in every state, with the state on it (`data-mode`) for the end-to-end journey.
  const page = (children: React.ReactNode) => (
    <Box sx={PAGE} data-testid="capability-setup" data-mode={mode}>
      {children}
    </Box>
  );
  // DUMMY: said whenever the setup is kept in this browser only.
  const localBanner = mode === 'local' && (
    <Box
      role="note"
      sx={{
        flexShrink: 0,
        px: '14px',
        py: '8px',
        borderRadius: '4px',
        border: '1px dashed rgba(247, 148, 32, 0.6)',
        bgcolor: BRANDY_TINT
      }}
    >
      <Typography sx={{ ...BODY, fontSize: 12.5, color: BRANDY }}>
        Your changes are saved on this device only for now.
      </Typography>
    </Box>
  );
  // In place of the page: what it is waiting on, or why there is nothing to set up.
  const instead = (state: React.ReactNode) =>
    page(
      <>
        {localBanner}
        <Box sx={{ ...CARD, flex: 1, display: 'flex' }}>{state}</Box>
      </>
    );

  if (mode === 'loading') return page(<PageSkeleton card={CARD} />);
  if (mode === 'error') {
    if (loadError?.status === 403) {
      return instead(
        <EmptyState
          title="Capabilities is for Org Admins and Managers."
          body="If you manage a team, ask an Org Admin to add you as its Manager."
        />
      );
    }
    // A 404 from an API without the endpoints: the backend for this page isn't deployed yet.
    const unavailable = Boolean(loadError?.missingEndpoints);
    return instead(
      <EmptyState
        title={unavailable ? 'Capabilities is not available yet.' : 'Capabilities could not be loaded.'}
        body={unavailable ? 'Try again in a little while.' : 'Check your connection and try again.'}
      >
        <ActionButton primary onClick={reload}>
          Try Again
        </ActionButton>
      </EmptyState>
    );
  }

  const chooseTeam = (id: string) => {
    setTeamId(id);
    setRoleId(null);
    setAddingRole(false);
  };
  // Build Assessment / Build Training, over the page: from a team, or from a shared set (with its preset).
  const creationDialog = flowsReady && (
    <CreationFlowDialog
      kind={creating}
      roles={roles}
      sets={sets}
      sharedPreset={sharedPreset}
      teamsListId={(mode === 'local' ? localTeams.teamsList?.listId : teamsList?.listId) ?? null}
      // The backend marks the measured roles launched when the Assessment runs: read them again.
      onAssessmentRun={() => reload()}
      onTrainingFinished={(groupId) => {
        // Built and its people invited: to the dashboard, where its card is scrolled to and highlighted.
        setCreating(null);
        setSharedPreset(null);
        navigate(`/app/programdetails/${orgId}/${coId}/admin/overview`, {
          state: { highlightProgramId: groupId }
        });
      }}
      onClose={() => {
        setCreating(null);
        setSharedPreset(null);
      }}
    />
  );

  // An org admin's shared set (Development or Standards): the same page, for the whole organization.
  // With no team to show, it is where an org admin starts.
  const sharedInView = sharedKind ?? (!team && sharedChoices.length > 0 ? SHARED_KINDS[0] : null);
  if (sharedInView) {
    return page(
      <>
        <SharedSetView
          // Fresh for each set: nothing typed, asked of Elliott or opened carries over to the other one.
          key={sharedInView}
          kind={sharedInView}
          sets={sets}
          roles={roles}
          teams={teams}
          switcher={
            <TeamSwitch
              teams={teamChoices}
              shared={sharedChoices}
              value={sharedSetId(sharedInView)}
              onChange={chooseTeam}
            />
          }
          saveSet={saveSet}
          saving={capabilitySets.isSaving(sharedInView)}
          onBuild={
            flowsReady
              ? (what, preset) => {
                  setSharedPreset(preset);
                  setCreating(what);
                }
              : undefined
          }
          trainingReady={trainingReady}
        />
        {creationDialog}
      </>
    );
  }

  // No team to set up: say why, in place of the page.
  if (!team) {
    const teamsError = mode === 'local' ? localTeams.error : null;
    if (mode === 'local' && localTeams.loading) return page(<PageSkeleton card={CARD} />);
    return instead(
      teamsError ? (
        <EmptyState title="Your teams could not be loaded." body="Check your connection and try again." />
      ) : teams.length > 0 ? (
        <EmptyState
          title="The teams you manage will appear here."
          body="Ask an Org Admin to add you as a team's Manager."
        />
      ) : (
        <EmptyState
          title="No teams yet"
          body="Capabilities are set up team by team. Add your teams in Settings and they will appear here."
        />
      )
    );
  }

  const drafting = Boolean(role && draftingRoleId === role.id);
  const noneReady = 'Confirm the capabilities and levels of at least one role first.';
  // Set up already, with a capability of its own added since its levels were confirmed (saved, or in
  // the edits): it waits for the levels to be confirmed again before it counts.
  const confirmedIds = new Set(saved ? confirmedIdsOf(saved) : []);
  const waiting = role && saved && isSettled(saved) ? role.capabilities.filter((c) => !confirmedIds.has(c.id)) : [];
  const savingReason = 'Saving your last change.';
  const sameText = (a?: string, b?: string) => String(a || '').trim() === String(b || '').trim();
  // What a confirm judges, as the backend does: a capability confirmed before and unchanged stands.
  const judged = (c: SetupCapability) => {
    const prior = saved?.capabilities.find((p) => p.id === c.id);
    const unchanged = prior && LEVEL_ORDER.every((l) => sameText(prior.criteria[l], c.criteria[l]));
    return !(unchanged && confirmedIds.has(c.id));
  };

  /** Elliott's flags that still apply to a capability: its level reads as he checked it, and isn't his own suggestion. */
  const elliottFlags = (c: SetupCapability): LevelCheckView[] => {
    const stored = storedLevelChecks.data?.checks.find(
      (s) => s.capabilityId === c.id && LEVEL_ORDER.every((l) => sameText(s.criteria[l], c.criteria[l]))
    );
    const latest = levelChecks[c.id] ?? (stored ? { criteria: stored.criteria, levels: stored.levels } : null);
    if (!latest) return [];
    return latest.levels.filter(
      (f) =>
        !f.ok &&
        sameText(latest.criteria[f.level], c.criteria[f.level]) &&
        !(`${c.id}:${f.level}` in acceptedFixes && sameText(acceptedFixes[`${c.id}:${f.level}`], c.criteria[f.level]))
    );
  };
  // What holds the levels back: one that joins two skills, the second named by Elliott. Everything else is advice.
  const heldBack = role
    ? role.capabilities.flatMap((c) =>
        elliottFlags(c)
          .filter(namesSecondSkill)
          .map((f) => `${c.name}, ${LEVEL_NAME[f.level]}`)
      )
    : [];
  /**
   * Asks Elliott about every capability this action would confirm, before it does.
   * True when he reads a level as two things, which holds it back. A capability he
   * couldn't be asked about isn't held back: the block is his verdict, not his availability.
   */
  const elliottHoldsBack = async (): Promise<boolean> => {
    if (!role) return false;
    const asking = role.capabilities.filter(
      (c) => judged(c) && LEVEL_ORDER.every((l) => String(c.criteria[l] || '').trim())
    );
    if (asking.length === 0) return false;
    setCheckingLevels(true);
    setLevelCheckError(null);
    const results = await Promise.allSettled(
      asking.map((c) => {
        const asked = { ...c.criteria };
        return capabilityApi
          .checkLevels({ roleName: role.name, capabilityName: c.name, criteria: asked, capabilityId: c.id }, org)
          .then((r) => ({ capability: c, asked, levels: r.levels }));
      })
    );
    setCheckingLevels(false);
    const answered = results.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : []));
    if (answered.length < results.length) {
      setLevelCheckError("Elliott couldn't check every level just now, so those weren't held back.");
    }
    setLevelChecks((current) => ({
      ...current,
      ...Object.fromEntries(answered.map((a) => [a.capability.id, { criteria: a.asked, levels: a.levels }]))
    }));
    return answered.some((a) =>
      a.levels.some((f) => {
        const key = `${a.capability.id}:${f.level}`;
        return !f.ok && namesSecondSkill(f) && !(key in acceptedFixes && sameText(acceptedFixes[key], a.asked[f.level]));
      })
    );
  };
  /** Confirm Levels, once Elliott has passed them. */
  const confirmLevelsChecked = async () => {
    if (!role || (await elliottHoldsBack())) return;
    saveRole(role.id, confirmLevels);
  };
  /** Save Changes; when the levels stay confirmed, once Elliott has passed them. */
  const saveChangesChecked = async () => {
    if (role?.levelsConfirmed && (await elliottHoldsBack())) return;
    saveChanges();
  };
  const { managers } = team;
  // An edit from an answer of Elliott's: made to the role's draft as it is when the answer
  // arrives (whatever was typed meanwhile stays), from the saved role if there is none.
  const editWhenAnswered = (base: SetupRole, change: (r: SetupRole) => SetupRole | null) =>
    setDrafts((current) => {
      const result = change(current[base.id]?.role ?? base);
      return result ? { ...current, [base.id]: { base: current[base.id]?.base ?? base, role: result } } : current;
    });
  // More capabilities for the role in view, from Elliott, added in one go.
  const suggestMore = async () => {
    if (!role || !saved) return;
    const base = saved;
    setSuggesting(true);
    try {
      // Not the names the team's other roles use either: one of those could only be imported, not saved as this role's own.
      const { capabilities } = await capabilityApi.suggestCapabilities(
        { roleName: role.name, teamName: team.name, existing: suggestionNamesTaken(roles, role) },
        org
      );
      setElliottError(null);
      editWhenAnswered(base, (r) => capabilities.reduce<SetupRole>((acc, name) => addCapability(acc, name) ?? acc, r));
    } catch (err: any) {
      if (err?.response?.status === 503) setElliottOn(false);
      setElliottError(apiMessage(err, 'Elliott could not suggest capabilities.'));
    } finally {
      setSuggesting(false);
    }
  };
  // A capability added once the list is confirmed gets Elliott's levels for it (the role's
  // edits wait for them, as when the list was confirmed).
  const addRoleCapability = (name: string): boolean => {
    if (!role || !saved || !editRole((r) => addCapability(r, name))) return false;
    if (!role.capabilitiesConfirmed || !elliottOn) return true;
    const base = saved;
    setDraftingRoleId(base.id);
    draftLevelsFor(role.name, team.name, [name.trim()])
      .then((drafted) => {
        const levels = drafted.get(name.trim().toLowerCase());
        if (levels) editWhenAnswered(base, (r) => fillLevels(r, name, levels));
      })
      .finally(() => setDraftingRoleId((id) => (id === base.id ? null : id)));
    return true;
  };

  // On the section's bar, right: add a capability, import one from another role of the team, or ask Elliott.
  const atLimit = Boolean(role && role.capabilities.length >= MAX_CAPABILITIES);
  const limitReason = `A role can have at most ${MAX_CAPABILITIES} capabilities of its own.`;
  const addActions = role && !drafting && (
    <Box sx={{ ml: '12px', display: 'flex', [MOBILE_SX]: { ml: 0, flexBasis: '100%' } }}>
      <AddForm
        key={role.id}
        compact
        autoFocus={role.capabilities.length === 0}
        label="Capability name"
        placeholder={role.capabilities.length === 0 ? 'e.g. Objection handling' : 'Add another capability'}
        button="Add Capability"
        maxLength={MAX_NAME}
        blocked={atLimit ? limitReason : undefined}
        onAdd={addRoleCapability}
        refused={(name) => `${role.name} already has a capability called ${name}.`}
      >
        {/* Share a capability another role of this team already has. */}
        <ActionButton
          disabled={atLimit || !teamRoles.some((r) => r.id !== role.id && r.capabilities.length > 0)}
          reason={atLimit ? limitReason : `No other role in ${team.name} has capabilities to import yet.`}
          onClick={() => setImporting(true)}
        >
          Import Capability
        </ActionButton>
        {elliottOn && !role.capabilitiesConfirmed && (
          <ActionButton
            disabled={suggesting || atLimit}
            reason={suggesting ? 'Elliott is thinking of capabilities for this role.' : limitReason}
            onClick={suggestMore}
          >
            {suggesting ? 'Suggesting...' : 'Suggest With Elliott'}
          </ActionButton>
        )}
      </AddForm>
    </Box>
  );

  // Edits wait on Save Changes; until then it takes the place of the stage's confirmation.
  const saveButtons = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Typography sx={{ ...LABEL, color: BRANDY, mr: '4px' }}>{saving ? 'Saving' : 'Unsaved Changes'}</Typography>
      <ActionButton disabled={saving} reason={savingReason} onClick={() => role && dropDraft(role.id)}>
        Discard
      </ActionButton>
      <ActionButton
        primary
        disabled={saving || checkingLevels || Boolean(problem)}
        reason={saving ? savingReason : problem ?? undefined}
        onClick={saveChangesChecked}
      >
        {checkingLevels ? 'Elliott is checking...' : 'Save Changes'}
      </ActionButton>
    </Box>
  );
  // Confirm Levels asks Elliott first, and confirms once he passes them.
  const confirmLevelsButton = role ? (
    <ActionButton
      primary
      disabled={saving || checkingLevels || !canConfirmLevels(role)}
      reason={saving ? savingReason : checkingLevels ? undefined : 'Every capability needs all four levels.'}
      onClick={confirmLevelsChecked}
    >
      {checkingLevels ? 'Elliott is checking...' : 'Confirm Levels'}
    </ActionButton>
  ) : null;

  // The stage the role is at, with its one confirmation: along the bottom of the section.
  let note: React.ReactNode = null;
  if (role && drafting) {
    note = (
      <ElliottNote
        title="Elliott is drafting the levels"
        body={`Writing what Weak, Developing, Capable and Advanced look like for each of ${role.name}'s capabilities.`}
      />
    );
  } else if (role && !role.capabilitiesConfirmed) {
    // With none of its own, the role is confirmed on what the organization requires for it.
    const sharedOnly = role.capabilities.length === 0 && applied.length > 0;
    note = (
      <ElliottNote
        title={
          sharedOnly
            ? `Review the capabilities your organization requires for ${role.name}, add any of its own, then confirm`
            : `Choose the capabilities that matter most for doing the ${role.name} role well, then confirm`
        }
        body={
          sharedOnly
            ? `With no capabilities of its own, confirming them sets ${role.name} up: there are no levels of its own to write.`
            : undefined
        }
      >
        {dirty ? (
          saveButtons
        ) : (
          <ActionButton
            primary
            disabled={saving || !canConfirmCapabilities(role, applied.length)}
            reason={saving ? savingReason : 'Add at least one capability first.'}
            onClick={() => confirmRoleCapabilities(role.id)}
          >
            Confirm Capabilities
          </ActionButton>
        )}
      </ElliottNote>
    );
  } else if (role && heldBack.length > 0) {
    // Elliott reads a level as two things: nothing is confirmed, or set up, until it names one.
    note = (
      <ElliottNote
        title={
          heldBack.length === 1
            ? `Elliott flagged ${heldBack[0]}`
            : `Elliott flagged ${heldBack.length} levels: ${listNames(heldBack)}`
        }
        body={`${heldBack.length === 1 ? 'Update it' : 'Update them'}, then ${
          role.levelsConfirmed ? 'save your changes' : 'confirm'
        }. Levels are confirmed once each one names one specific thing.`}
      >
        {dirty ? saveButtons : role.levelsConfirmed ? null : confirmLevelsButton}
      </ElliottNote>
    );
  } else if (role && !role.levelsConfirmed) {
    // Elliott drafts the levels where it can; elsewhere (production) they start empty and the manager writes them.
    note = (
      <ElliottNote
        title={
          elliottOn ? 'Review the levels, then confirm' : 'Write the four levels for each capability, then confirm'
        }
        // The same guidance whether Elliott drafted the levels or the manager writes them.
        body="Ensure 'Weak' captures a specific behavior that an employee who is weak at a capability would exhibit, and repeat for all levels. This allows Elliott to generate relevant training and assessments that drive people toward a shared standard of Advanced at all of your role's capabilities."
      >
        {dirty ? saveButtons : confirmLevelsButton}
      </ElliottNote>
    );
  } else if (role) {
    note = (
      <ElliottNote
        done={!dirty}
        title={`${role.name} is set up`}
        body={
          flowsReady
            ? `You can build an Assessment${
                trainingReady ? ' or a Training' : ''
              } on these capabilities at any time from the bar above, and come back to edit the capabilities or their levels.`
            : 'You can come back at any time to edit the capabilities or their levels.'
        }
      >
        {dirty && saveButtons}
      </ElliottNote>
    );
  }

  return page(
    <>
      {localBanner}
      {/* The top bar. Left: ROLES | team | the team's roles (the navigation: one
      role in view at a time), scrolling sideways when there are more than fit.
      Middle: the team's setup stepper. Right: the standing actions. */}
      <Box
        component="section"
        sx={{
          ...CARD,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px 16px',
          flexShrink: 0,
          minHeight: 60,
          px: '16px',
          py: '10px',
          ...fadeInSx,
          // A phone: one row each for the roles, the stepper and the actions.
          [MOBILE_SX]: {
            flexDirection: 'column',
            flexWrap: 'nowrap',
            alignItems: 'stretch',
            gap: '12px',
            p: '12px',
            minWidth: 0
          }
        }}
      >
        <Box
          sx={{ flex: '1 1 320px', minWidth: 0, display: 'flex', alignItems: 'center', [MOBILE_SX]: { flex: 'none' } }}
        >
          <TeamSwitch teams={teamChoices} shared={sharedChoices} value={team.id} onChange={chooseTeam} />
          {DIVIDE}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              // More roles than fit: scroll them left to right, rather than push into the stepper.
              overflowX: 'auto',
              py: '2px',
              scrollbarWidth: 'thin',
              scrollbarColor: '#565656 transparent',
              '&::-webkit-scrollbar': { height: '4px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: '#565656', borderRadius: '2px' },
              // A phone: the roles swipe sideways, without a scrollbar.
              [MOBILE_SX]: { scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }
            }}
          >
            {teamRoles.map((r) => {
              // Set up (ready): confirmed once, for good. A capability added since waits for its levels.
              const confirmed = isRoleUsable(r, sets);
              const status = !confirmed
                ? 'not yet confirmed'
                : levelsDone(r)
                ? 'capabilities and levels confirmed'
                : 'set up, with changes to confirm';
              return (
                <Box
                  key={r.id}
                  component="button"
                  type="button"
                  aria-pressed={r.id === saved?.id}
                  aria-label={`${r.name}, ${status}`}
                  onClick={() => selectRole(r.id)}
                  sx={chipSx(r.id === saved?.id)}
                >
                  {r.name}
                  {/* A status mark, not a control: an empty ring until the role is
                  set up (its capabilities and levels confirmed; with only shared
                  capabilities, its capabilities). It stays once set up. */}
                  {confirmed ? (
                    <CheckCircleIcon sx={{ fontSize: 15, color: DONE, mr: '-3px' }} />
                  ) : (
                    <Box
                      aria-hidden
                      sx={{
                        width: 11,
                        height: 11,
                        flexShrink: 0,
                        borderRadius: '50%',
                        border: '1.5px solid #565656',
                        mr: '-1px'
                      }}
                    />
                  )}
                </Box>
              );
            })}
            {teamRoles.length === 0 || addingRole ? (
              <AddForm
                key={team.id}
                autoFocus
                label="New role name"
                placeholder="Role name, e.g. Account Executive"
                button="Add Role"
                maxLength={MAX_NAME}
                onAdd={addRole}
                refused={(name) => `${team.name} already has a role called ${name}.`}
                onCancel={teamRoles.length > 0 ? () => setAddingRole(false) : undefined}
              />
            ) : (
              <Box
                component="button"
                type="button"
                onClick={() => setAddingRole(true)}
                sx={{ ...chipSx(false), borderStyle: 'dashed', color: BRANDY, fontWeight: 600 }}
              >
                Add Role
              </Box>
            )}
          </Box>
        </Box>

        <SetupStepper steps={steps} teamName={team.name} />

        {flowsReady && (
          <Box
            sx={{
              flex: '0 0 auto',
              display: 'flex',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              gap: '8px',
              // A phone: the actions share the bar's width, two to a row.
              [MOBILE_SX]: { '& > span': { flex: '1 1 40%' }, '& > span > button': { width: '100%', minHeight: 40 } }
            }}
          >
            <ActionButton
              primary={readyRoles.length > 0}
              disabled={readyRoles.length === 0}
              reason={noneReady}
              onClick={() => setCreating('assessment')}
            >
              Build Assessment
            </ActionButton>
            {trainingReady && (
              <ActionButton
                disabled={readyRoles.length === 0}
                reason={noneReady}
                onClick={() => setCreating('training')}
              >
                Build Training
              </ActionButton>
            )}
            <ActionButton
              disabled={readyRoles.length === 0}
              reason="You can invite people to a role once its capabilities and levels are confirmed."
              onClick={() => setInviting(true)}
            >
              Invite Team
            </ActionButton>
          </Box>
        )}
      </Box>

      {/* The main section: the role in view, its capabilities and their levels.
      Not shown until the team has a role: at that stage the page is only about
      adding roles. */}
      {role && saved && (
        <Box
          component="section"
          sx={{
            ...CARD,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            ...fadeInSx,
            [MOBILE_SX]: { flex: 'none', minHeight: 'auto' }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '8px 0',
              minHeight: 54,
              px: '16px',
              py: '8px',
              borderBottom: `1px solid ${LINE}`,
              // A phone: the header's parts wrap onto rows of their own, without the rules between them.
              [MOBILE_SX]: { gap: '10px 12px', p: '12px', '& > span[aria-hidden]': { display: 'none' } }
            }}
          >
            <Typography sx={SECTION_TITLE}>Capabilities and levels</Typography>
            {DIVIDE}
            <RoleHeader
              role={saved}
              actions={addActions}
              after={
                <>
                  {DIVIDE}
                  <Box
                    sx={{
                      minWidth: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      overflow: 'hidden',
                      // A phone: every manager in view, wrapping, on a row of their own.
                      [MOBILE_SX]: { flexBasis: '100%', flexWrap: 'wrap', rowGap: '6px', overflow: 'visible' }
                    }}
                  >
                    <Typography
                      sx={{ flexShrink: 0, fontFamily: FONT, fontSize: 13, color: TEXT_3, whiteSpace: 'nowrap' }}
                    >
                      {managers.length === 1 ? 'Team Manager' : 'Team Managers'}
                    </Typography>
                    {managers.length === 0 && (
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_2 }}>None yet</Typography>
                    )}
                    {managers.map((m) => (
                      <Box key={m.id} sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Avatar
                          alt=""
                          sx={{
                            width: 24,
                            height: 24,
                            fontFamily: FONT,
                            fontSize: 10,
                            fontWeight: 700,
                            color: BRANDY,
                            bgcolor: 'rgba(247, 148, 32, 0.14)',
                            border: BRANDY_FRAME
                          }}
                        >
                          {m.name
                            .split(' ')
                            .map((part) => part[0])
                            .slice(0, 2)
                            .join('')
                            .toUpperCase()}
                        </Avatar>
                        <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_2, whiteSpace: 'nowrap' }}>
                          {m.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              }
              refusal={(name) =>
                renameRole(roles, saved.id, name) ? null : 'That name is blank or already used in this team.'
              }
              onRename={(name) => {
                const renamed = renameRole(roles, saved.id, name)?.find((r) => r.id === saved.id);
                return renamed
                  ? saveRole(saved.id, (latest) => ({ ...latest, name: renamed.name }))
                  : Promise.resolve(false);
              }}
              onDelete={async () => {
                const gone = await deleteRole(saved.id);
                if (gone) {
                  dropDraft(saved.id);
                  setRoleId(null);
                }
                return gone;
              }}
            />
          </Box>

          {/* The capabilities scroll here, between the header and the bottom bar. */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              p: '16px',
              scrollbarWidth: 'thin',
              scrollbarColor: '#565656 transparent',
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: '#565656', borderRadius: '4px' },
              [MOBILE_SX]: { flex: 'none', overflowY: 'visible', p: '12px' }
            }}
          >
            {role.capabilities.length === 0 && applied.length === 0 && (
              <EmptyState
                title="No capabilities yet"
                body={`Add ${role.name}'s first capability above, import one from another role in ${team.name}, or let Elliott suggest some.`}
              />
            )}
            {role.capabilities.map((c, i) => {
              // Added under a name a team-mate role already uses with levels of its own: it can be that one, shared.
              const clash = dirty && saved ? clashToImport(roles, saved, role, c.id) : null;
              return (
                <CapabilityRow
                  key={c.id}
                  number={i + 1}
                  capability={c}
                  source="role"
                  roleName={role.name}
                  levelsOpen={Boolean(role.capabilitiesConfirmed)}
                  elliott={{
                    flags: elliottFlags(c),
                    checking: checkingLevels,
                    error: levelCheckError,
                    onAccept: (level, text) => setAcceptedFixes((current) => ({ ...current, [`${c.id}:${level}`]: text }))
                  }}
                  drafting={drafting}
                  problem={capabilityNameProblem(role, c.id) ?? teamProblemOf(c.id)}
                  sharedWith={sharedNames(c.id)}
                  // Added since the role was set up: it counts once the levels are confirmed again.
                  tag={waiting.some((w) => w.id === c.id) ? 'Counts once the levels are confirmed' : undefined}
                  importFrom={
                    clash
                      ? {
                          roleName: clash.role.name,
                          onImport: () => editRole((r) => withImported(r, c.id, clash.capability))
                        }
                      : undefined
                  }
                  onRename={(name) => editRole((r) => renameCapability(r, c.id, name))}
                  onRemove={() => editRole((r) => removeCapability(r, c.id))}
                  onCriteria={(level, text) => editRole((r) => setCriteria(r, c.id, level, text))}
                />
              );
            })}

            {/* The organization's shared capabilities applied to this role: set by an org admin and
            read here. The team confirms the role, never these. */}
            {applied.length > 0 && (
              <>
                <Typography sx={{ ...LABEL, pt: role.capabilities.length > 0 ? '8px' : 0 }}>
                  Required by your organization
                </Typography>
                {applied.map((c, i) => (
                  <CapabilityRow
                    key={c.id}
                    number={role.capabilities.length + i + 1}
                    capability={c}
                    source={sourceOf(c)}
                    readOnly
                    levelsOpen
                    drafting={false}
                    tag={`Set by ${c.setBy?.name || 'your Org Admin'} as a capability required for this role`}
                  />
                ))}
              </>
            )}
          </Box>

          {/* The bottom bar, always in view: the confirmation for the stage the role
          is at (capabilities, then levels), then that the role is set up. */}
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              p: '12px 16px',
              borderTop: `1px solid ${LINE}`,
              [MOBILE_SX]: { p: '12px' }
            }}
          >
            {elliottError && (
              <Typography role="alert" sx={{ ...BODY, fontSize: 12.5, color: '#FF6A55' }}>
                {elliottError}
              </Typography>
            )}
            {note}
          </Box>
        </Box>
      )}

      {role && (
        <ImportCapabilitiesDialog
          open={importing}
          role={role}
          roles={roles}
          teamName={team?.name ?? 'this team'}
          onClose={() => setImporting(false)}
          onImport={(choices) => editRole((r) => importCapabilities(r, choices))}
        />
      )}
      {role && (
        <SharedCapabilityDialog
          edits={sharedToRename}
          role={role}
          roles={roles}
          onClose={() => setSharedToRename([])}
          onRename={(names) => {
            setSharedToRename([]);
            saveChanges(names);
          }}
        />
      )}

      {flowsReady && (
        <>
          <InviteTeamDialog
            open={inviting}
            team={{ id: team.id, name: team.name }}
            onClose={() => setInviting(false)}
            // Someone added or invited ticks the Invite Team step: read the teams again.
            onChanged={() => refresh()}
          />
          {creationDialog}
        </>
      )}
    </>
  );
};

export default CapabilitySetupPage;
